import { Injectable } from '@angular/core';
import { Http, Response, Headers, RequestOptions, URLSearchParams } from '@angular/http';
import { Observable } from 'rxjs/Observable';

// Import de nm-socle-application
import { NotificationService } from '../components/notification/notification.service';
import { ConfigService } from './config.service';
import { SecurityService } from './security.service';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import { TranslateService } from 'ng2-translate';
import { ErrorObservable } from 'rxjs/observable/ErrorObservable';

import { Subject } from 'rxjs/Subject';

/**
 * Service d'interactions avec GeoServices
 */
@Injectable()
export class GeoServices {
    private _params: URLSearchParams = null;
    private _mapEpsg: string = '';

    // Liste des requêtes en cours (par mapping associatif, but->s'en servir pour virer toutes les variables de spinner des composants)
    public requestPendingList: any = {};
    
    // Enables to expose the list of pending requests through an observable
    private _requestPendingList$ = new Subject();

    /* Variable permettant de savoir si une requete en général est en cours (la liste dessus permet de savoir si une requete spécifique est en cours)*/
    public isRequesting: boolean = false;

    /**
     * Creates an instance of GeoServices.
     * Service de connexion à l'api Maps de GeoService
     *
     * @param {Http} http service http angular
     * @param {SecurityService} _securityService service de securite
     * @param {ConfigService} _configService service de configuration
     * @param {CryptoService} _cryptoService service de crypto
     *
     * @memberOf GeoServices
     */
    constructor(
        private _http: Http,
        private _securityService: SecurityService,
        private _configService: ConfigService,
        private _translateService: TranslateService
    ) {
        this._params = new URLSearchParams();
    }

    /**
     * Expose the list of pending requests through an observable
     * @return : the list of pending request as an Observable
     */
    public watchRequestPendingList(): Observable<any> {
        return this._requestPendingList$;
    }

    public setEPSG(code: string) {
        this._mapEpsg = code;
    }

    public get mapEpsg() {
        return this._mapEpsg;
    }

    /**
     * Effectue une requete qque sur le backend, positionne automatiquement
     * les headers nécessaires à l'authentification WSSE
     *
     * @private
     * @param {string} uri clé de l'url  dans la configuration
     * @param {string} method methode HTTP
     * @param {Array<any>} headers headers additionnels nécessaires
     * @param {any} parameters paramètres de route
     * @param {any} data données
     * @returns Observable<Response>
     *
     * @memberOf GeoServices
     */
    public doRequest(uri: string, method: string, headers?: Array<any>, parameters?: any, data?: Object, responseType?: any, authenticate: boolean = true) {

        this._securityService.updateLastCall();

        // chargement du numero de client pour ajout en parametre
        if (this._configService.get('customerId')) {
            this._params.set('customerId', this._configService.get('customerId'));
        }

        // calcul des headers WSSE
        let username = this._configService.get('login');
        let tokenId = this._configService.get('pwd');
        let securityParams = this._securityService.getParameters();

        let wsseHeaders = new Headers();
        if (authenticate && username && tokenId && Object.keys(securityParams).length > 0) {
            let wsseHeader = this._securityService.getWSSEHeader(username, tokenId);
            wsseHeaders.append('Authorization', 'WSSE profile="UsernameToken"');
            wsseHeaders.append('X-WSSE', wsseHeader);
        }

        // ajout des headers supplémentaires
        if (!headers) {
            headers = [];
            headers.push({ 'Accept': 'application/json, text/plain, */*' });
        }
        for (let headerObject of headers) {
            for (let item in headerObject) {
                wsseHeaders.append(item, headerObject[item]);
            }
        }

        // remplacement des paramètres de route
        for (let param in parameters) {
            let value = parameters[param];
            let paramRgx = new RegExp(param, 'g');
            uri = uri.replace(paramRgx, value);
        }

        // let's go
        let requestOptions = new RequestOptions({
            method: method,
            headers: wsseHeaders,
            body: data,
            search: this._params,
            responseType: responseType
        });
        return this._http.request(uri, requestOptions);
    }


    /**
     * Requête les features d'un layer par une liste d'ids
     *
     * @param {string} uri endpoint pour la requete
     * @param {Array<any>} featureIdList Liste d'ids de feature
     * @param {*} layerId Identifiant de layer
     * @returns Observable<Response>
     *
     * @memberOf GeoServices
     */
    public doFeatureRequestUriKey(uriKey: string, featureIdList: Array<any>, layerId: any, fieldName?: string, tableName?: string, theme?: string, messageLoading?: string, urlExtraData?: object): any {

        this._securityService.updateLastCall();

        let headers = [];
        headers.push({ 'Accept': 'application/vnd.geo+json' });
        headers.push({ 'Cache-Control': 'no-cache' });
        headers.push({ 'Content-Type': 'application/json' });

        let data: any = {
            ids: featureIdList,
            layerId: layerId,
            epsg: this._mapEpsg
        };

        if (fieldName) {
            data.fieldName = fieldName;
            data.idPropertyName = fieldName;
        }

        if (tableName) {
            data.tableName = tableName;
        }

        if (urlExtraData) {
            data = merge({}, data, urlExtraData);
        }

        return this.doRequestUriKey(uriKey, 'post', headers, undefined, data, undefined, theme, messageLoading).share();
    }

    /**
     * Effectue une requete qque sur le backend
     *
     * @param {string} uri clé de l'url  dans la configuration
     * @param {Array<any>} headers headers additionnels nécessaires
     * @param {any} parameters paramètres de route
     * @param {any} data données
     * @param {any} responseType type de reponse
     * @param {string} theme 'theme de la requete' (par ex, Analyse, Impression...)
     * @param {string} messageLoading message affiché sur le composant de chargement en attente
     * @returns Observable<Response>
     *
     * @memberOf GeoServices
     */
    public doRequestUriKey(uriKey: string, uriMethod?: string, headers?: Array<any>, parameters?: any, data?: any, responseType?: any, theme?: string, messageLoading?: string) {

        this._securityService.updateLastCall();

        this.isRequesting = true;
        let notification = null;

        if (theme && messageLoading) {
            if (!this.requestPendingList[theme]) {
                this.requestPendingList[theme] = 0;
            }
            this.requestPendingList[theme]++;
            this._requestPendingList$.next(this.requestPendingList);
            notification = NotificationService.loading(messageLoading, theme);
        }

        let uri = this._configService.get(uriKey).value;
        let method = uriMethod || this._configService.get(uriKey).method;
        let authenticate = this._configService.get(uriKey).authenticate === false ? false : true;
        let request = this.doRequest(uri, method, headers, parameters, data, responseType, authenticate).share();

        request.subscribe(
            res => {
                this.changeValueIsRequesting();
                if (theme && messageLoading) {
                    this.requestPendingList[theme]--;
                    this._requestPendingList$.next(this.requestPendingList);
                    NotificationService.remove(notification);
                }
            },
            err => {
                this.changeValueIsRequesting();
                if (theme && messageLoading) {
                    this.requestPendingList[theme]--;
                    this._requestPendingList$.next(this.requestPendingList);
                    NotificationService.remove(notification);
                }
            });
        return request;
    }

    private changeValueIsRequesting() {
        if (!isEmpty(this.requestPendingList)) {
            let item: any;
            for (item in this.requestPendingList) {
                if (item > 0) {
                    this.isRequesting = true;
                    break;
                }
            }
            this.isRequesting = false;
        }
        else {
            this.isRequesting = false;
        }
    }


    /**
     * Extrait la réponse en json
     *
     * @param {Response} res
     *
     * @memberOf GeoServices
     */
    public extractData(res: Response): any {
        let body = res.json();
        return body || {};
    }

    /**
     * Extrait la réponse en json et re parse en JSON
     *
     * @param {Response} res
     *
     * @memberOf GeoServices
     */
    public extractDataParseJSON(res: Response): any {
        let body = res.json();
        try {
            let contenu = JSON.parse(body);
            return contenu || {};
        } catch (ex) {
            return {};
        }
    }

    /**
     * Gére les erreurs (A tout les niveaux)
     *
     * @param {any} error une erreur
     * @returns{ErrorObservable.create} L'observable de la requête
     *
     * @memberOf GeoServices
     */
    public handleError(error: any, notify: boolean = true): ErrorObservable {

        let errorMessage;
        let hiddenMessage;
        let title;

        try {
            let errorContent = error.json();
            errorMessage = errorContent.message;
        } catch (ex) {
            errorMessage = error.statusText || error.message;
        }

        // TODO : pour le moment le back a un gestion d'erreurs trop basique pour envoyer des messages pertinents
        if (error.status === 500) {
            hiddenMessage = errorMessage;
            title = this._translateService.instant('geo.notifications.internal-error');
            errorMessage = this._translateService.instant('geo.notifications.gis-internal-error');
        }

        if (notify) {
            NotificationService.alert(errorMessage, title, hiddenMessage);
        }

        return Observable.throw(errorMessage);
    }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/VyQQATNu/0/groupe-dev/nm17/node_modules/nm-socle-application/app/services/geo.service.ts